import { useMemo } from "react";

export const useFilteredFolders = (
  folders: any,
  searchTerm: any,
  selectedPath: any,
  sortBy = "name",
  sortOrder = "asc"
) => {
  const filteredFolders = useMemo(() => {
    let filtered = folders;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(
        (folder: any) =>
          (folder.name || folder.path || "")
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          (folder.path || "").toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by selected path
    if (selectedPath) {
      filtered = filtered.filter((folder: any) =>
        (folder.path || "").includes(selectedPath)
      );
    }

    // Sort the filtered results
    if (sortBy && sortOrder) {
      filtered = [...filtered].sort((a, b) => {
        let aValue = "";
        let bValue = "";

        // Get the value to sort by
        if (sortBy === "name") {
          // Use the actual name field for sorting (contains project codes)
          // but fall back to displayName if name doesn't exist
          aValue = a.name || a.displayName || "";
          bValue = b.name || b.displayName || "";
        }

        // Custom sorting logic for project names
        const getSortKey = (name: any) => {
          // Clean the name and trim any whitespace
          const cleanName = name.trim();

          // Extract leading number if exists (e.g., "255" from "255 - Ruiz Galindo")
          const numMatch = cleanName.match(/^(\d+(?:\.\d+)?)\s*-/);
          if (numMatch) {
            const num = parseFloat(numMatch[1]);
            return { type: "number", value: num, full: cleanName };
          }

          // Extract letter-number combination (e.g., "D284" from "D284 - Holt")
          const letterNumMatch = cleanName.match(
            /^([A-Za-z]+)(\d+(?:\.\d+)?)\s*-/
          );
          if (letterNumMatch) {
            const letter = letterNumMatch[1].toUpperCase();
            const num = parseFloat(letterNumMatch[2]);
            return {
              type: "letterNumber",
              letter,
              value: num,
              full: cleanName,
            };
          }

          // Check for pure numbers at start (without dash)
          const pureNumMatch = cleanName.match(/^(\d+(?:\.\d+)?)/);
          if (pureNumMatch) {
            const num = parseFloat(pureNumMatch[1]);
            return { type: "number", value: num, full: cleanName };
          }

          // Check for letter-number without dash
          const letterNumNoDashMatch = cleanName.match(
            /^([A-Za-z]+)(\d+(?:\.\d+)?)/
          );
          if (letterNumNoDashMatch) {
            const letter = letterNumNoDashMatch[1].toUpperCase();
            const num = parseFloat(letterNumNoDashMatch[2]);
            return {
              type: "letterNumber",
              letter,
              value: num,
              full: cleanName,
            };
          }

          // Everything else (text-only)
          return {
            type: "text",
            value: cleanName.toLowerCase(),
            full: cleanName,
          };
        };
        const aKey = getSortKey(aValue);
        const bKey = getSortKey(bValue);

        // Sort by type first: numbers, then letter-numbers, then text
        const typeOrder: any = { number: 0, letterNumber: 1, text: 2 };
        const aTypeOrder = typeOrder[aKey.type];
        const bTypeOrder = typeOrder[bKey.type];

        if (aTypeOrder !== bTypeOrder) {
          return sortOrder === "asc"
            ? aTypeOrder - bTypeOrder
            : bTypeOrder - aTypeOrder;
        }

        // Within the same type, sort appropriately
        if (aKey.type === "number") {
          return sortOrder === "asc"
            ? aKey.value - bKey.value
            : bKey.value - aKey.value;
        } else if (aKey.type === "letterNumber") {
          // First sort by letter, then by number
          if (aKey.letter !== bKey.letter) {
            return sortOrder === "asc"
              ? aKey.letter.localeCompare(bKey.letter)
              : bKey.letter.localeCompare(aKey.letter);
          }
          return sortOrder === "asc"
            ? aKey.value - bKey.value
            : bKey.value - aKey.value;
        } else {
          // Text sorting
          return sortOrder === "asc"
            ? aKey.value.localeCompare(bKey.value)
            : bKey.value.localeCompare(aKey.value);
        }
      });
    }

    return filtered;
  }, [folders, searchTerm, selectedPath, sortBy, sortOrder]);

  // Get unique paths for filtering
  const uniquePaths = useMemo(() => {
    const paths = new Set();
    folders.forEach((folder: any) => {
      if (folder.path) {
        const pathParts = folder.path.split("/").filter(Boolean);
        pathParts.forEach((_part: any, index: any) => {
          if (index < pathParts.length - 1) {
            paths.add(pathParts.slice(0, index + 1).join("/"));
          }
        });
      }
    });
    return Array.from(paths).sort();
  }, [folders]);

  return {
    filteredFolders,
    uniquePaths,
  };
};
